// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SearchEventRequest } from "@eisinc/App/CoveoConnector/Types/schema";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

export type AnalyticsObjectItemModel = {
    documentAuthor?: string;
    documentTitle: string;
    documentUrl: string;
    id: string;
    permanentId: string;
    rankingModifier?: string;
    title?: string;
    url?: string;
    uriHash?: string;
};

export type AnalyticsObjectModel = {
    advancedQuery: string;
    numberOfResults: number;
    queryPipeline: string;
    queryText: string;
    responseTime: number;
    searchQueryUid: string;
    items: AnalyticsObjectItemModel[];
    sourceName?: string;
};

export type xCoveoHandlerType = {
    trackCoveo?: boolean;
    analyticsObject?: AnalyticsObjectModel;
    query?: string;
    shouldSendInterfaceLoad?: boolean;
    // Store to use after getting products
    lastFacetDeSelectEvent?: SearchEventRequest;
    lastDidyoumeanClickEvent?: SearchEventRequest;
    searchEventParameters?: SearchEventRequest;
    facetSelectEventParameter?: SearchEventRequest;
    searchHub?: "search" | "listing" | "recs";
    facetClearAllTriggered?: boolean;
    shouldSendSearchFromLinkEvent?: boolean;
};

export type xCoveoStateType = Readonly<xCoveoHandlerType>;

const initialXCoveo: xCoveoStateType = {
    trackCoveo: false,
    analyticsObject: undefined,
    query: "",
    shouldSendInterfaceLoad: true,
    lastFacetDeSelectEvent: undefined,
    lastDidyoumeanClickEvent: undefined,
    searchEventParameters: undefined,
    facetSelectEventParameter: undefined,
    searchHub: undefined,
    facetClearAllTriggered: false,
    shouldSendSearchFromLinkEvent: false,
} as xCoveoStateType;

const reducer = {
    "xCoveo/update": (draft: Draft<xCoveoStateType>, action: { trackCoveo: boolean }) => {
        draft.trackCoveo = action.trackCoveo;
    },
    "xCoveo/storeSearchAnalyticsObject": (
        draft: Draft<xCoveoStateType>,
        action: { analyticsObject: AnalyticsObjectModel; query: string; searchHub: "search" | "listing" | "recs" },
    ) => {
        draft.analyticsObject = action.analyticsObject;
        draft.query = action.query;
        draft.searchHub = action.searchHub;
    },
    "xCoveo/updateShouldSendInterfaceLoad": (draft: Draft<xCoveoStateType>, action: { shouldSendInterfaceLoad: boolean }) => {
        draft.shouldSendInterfaceLoad = action.shouldSendInterfaceLoad;
    },
    "xCoveo/setLastFacetDeSelectEvent": (draft: Draft<xCoveoStateType>, action: { lastFacetDeSelectEvent: SearchEventRequest }) => {
        draft.lastFacetDeSelectEvent = action.lastFacetDeSelectEvent;
    },
    "xCoveo/setLastDidyoumeanClickEvent": (draft: Draft<xCoveoStateType>, action: { didyoumeanClickEvent: SearchEventRequest }) => {
        draft.lastDidyoumeanClickEvent = action.didyoumeanClickEvent;
    },
    "xCoveo/setSearchboxSubmitEvent": (draft: Draft<xCoveoStateType>, action: { searchEventParameters: SearchEventRequest }) => {
        draft.searchEventParameters = action.searchEventParameters;
    },
    "xCoveo/setfacetSelectEvent": (draft: Draft<xCoveoStateType>, action: { facetSelectEventParameter: SearchEventRequest }) => {
        draft.facetSelectEventParameter = action.facetSelectEventParameter;
    },
    "xCoveo/setFacetClearAllTriggered": (draft: Draft<xCoveoStateType>, action: { facetClearAllTriggered: boolean }) => {
        draft.facetClearAllTriggered = action.facetClearAllTriggered;
    },
    "xCoveo/setShouldSendSearchFromLinkEvent": (draft: Draft<xCoveoStateType>, action: { shouldSendSearchFromLinkEvent: boolean }) => {
        draft.shouldSendSearchFromLinkEvent = action.shouldSendSearchFromLinkEvent;
    },
};

const XCoveo = createTypedReducerWithImmer(initialXCoveo, reducer);

(reducers as any).xCoveo = XCoveo;

export type xCoveoState = {
    xCoveo: xCoveoStateType;
} & ApplicationState;

type xCoveoReducers = {
    [key: string]: typeof XCoveo;
};

export type xCoveoActions = Parameters<xCoveoReducers[keyof xCoveoReducers]>[1] | AnyAction;
